import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

const categories = ["All", "Habit Science", "Reading Lists", "Mindset", "Routines"];

const articles = [
  {
    id: 1,
    title: "Why 10 Pages Works When 50 Pages Doesn't",
    category: "Habit Science",
    date: "March 12, 2026",
    readTime: "5 min read",
    excerpt: "The paradox of ambitious goals is that they create resistance. Small targets, done consistently, bypass the brain's tendency to procrastinate.",
    img: "https://public.readdy.ai/ai/img_res/d470c101392ce5135029d8735ca81cdf.jpg",
    featured: true,
  },
  {
    id: 2,
    title: "The Science of Habit Stacking for Readers",
    category: "Habit Science",
    date: "March 6, 2026",
    readTime: "7 min read",
    excerpt: "Attach your reading to something you already do every day. Morning coffee. Commute. Lunch. The cue is already built — you just add the behavior.",
    img: "https://readdy.ai/api/search-image?query=person%20reading%20a%20book%20at%20a%20clean%20wooden%20table%20next%20to%20a%20cup%20of%20coffee%20in%20morning%20light%2C%20calm%20and%20focused%20atmosphere%2C%20minimal%20aesthetic%20neutral%20tones%2C%20lifestyle%20photography&width=800&height=500&seq=journal-2&orientation=landscape",
    featured: false,
  },
  {
    id: 3,
    title: "7 Books That Rewired How I Think",
    category: "Reading Lists",
    date: "Feb 28, 2026",
    readTime: "4 min read",
    excerpt: "Not productivity books. Not self-help. These are the books that fundamentally changed the mental models I use to move through the world.",
    img: "https://readdy.ai/api/search-image?query=seven%20carefully%20selected%20books%20arranged%20in%20a%20row%20on%20a%20minimalist%20wooden%20shelf%2C%20natural%20warm%20side%20light%2C%20clean%20neutral%20background%2C%20editorial%20flat%20lay%20photography&width=800&height=500&seq=journal-3&orientation=landscape",
    featured: false,
  },
  {
    id: 4,
    title: "Morning vs. Evening: When Should You Read?",
    category: "Routines",
    date: "Feb 20, 2026",
    readTime: "6 min read",
    excerpt: "There is no universally correct answer — but there is a correct answer for your specific cognitive rhythm. Here is how to find it.",
    img: "https://readdy.ai/api/search-image?query=open%20book%20on%20a%20nightstand%20with%20warm%20lamplight%20in%20the%20evening%2C%20calm%20and%20peaceful%20bedroom%20atmosphere%2C%20minimal%20and%20clean%20aesthetic%2C%20moody%20lifestyle%20photography&width=800&height=500&seq=journal-4&orientation=landscape",
    featured: false,
  },
  {
    id: 5,
    title: "How to Read Without Distraction in 2026",
    category: "Mindset",
    date: "Feb 14, 2026",
    readTime: "8 min read",
    excerpt: "Every notification is designed to pull you away. Reading deeply is an act of quiet resistance. These are the strategies that actually work.",
    img: "https://readdy.ai/api/search-image?query=person%20sitting%20alone%20reading%20a%20book%20in%20a%20calm%20minimalist%20room%20with%20no%20devices%20visible%2C%20natural%20daylight%20from%20window%2C%20peaceful%20focused%20atmosphere%2C%20lifestyle%20photography%20neutral%20tones&width=800&height=500&seq=journal-5&orientation=landscape",
    featured: false,
  },
  {
    id: 6,
    title: "30 Days In: What Readers Report",
    category: "Habit Science",
    date: "Feb 8, 2026",
    readTime: "5 min read",
    excerpt: "We asked 200 challenge completers what changed. The answers were more interesting than 'I read more books.' Focus. Sleep. Patience. Presence.",
    img: "https://readdy.ai/api/search-image?query=person%20writing%20in%20a%20journal%20at%20a%20clean%20minimalist%20desk%20with%20a%20cup%20of%20tea%2C%20calm%20morning%20atmosphere%2C%20soft%20warm%20natural%20light%2C%20focused%20and%20deliberate%20mood%2C%20lifestyle%20editorial%20photography&width=800&height=500&seq=journal-6&orientation=landscape",
    featured: false,
  },
];

export default function Journal() {
  const [activeCategory, setActiveCategory] = useState("All");

  const featured = articles.find((a) => a.featured);
  const rest = articles
    .filter((a) => !a.featured)
    .filter((a) => activeCategory === "All" || a.category === activeCategory);

  return (
    <main className="bg-[#F4F3F0]">
      <Navbar />

      {/* Header */}
      <section className="pt-32 pb-16 px-8 lg:px-24 bg-[#F4F3F0]">
        <div className="max-w-6xl mx-auto">
          <span className="text-[#3A5544] text-xs font-semibold uppercase tracking-[0.2em] mb-5 block">The Journal</span>
          <h1 className="font-serif text-5xl lg:text-6xl text-[#1A1A1A] leading-[1.1] max-w-2xl">
            Ideas on reading,<br />habit, and discipline.
          </h1>
        </div>
      </section>

      {/* Featured Article */}
      {featured && (
        <section className="px-8 lg:px-24 pb-10">
          <div className="max-w-6xl mx-auto">
            <div className="group grid grid-cols-1 lg:grid-cols-2 bg-white rounded-lg overflow-hidden cursor-pointer">
              <div className="h-64 lg:h-auto overflow-hidden">
                <img
                  src={featured.img}
                  alt={featured.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-10 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-5">
                  <span className="bg-[#3A5544] text-white text-xs font-medium px-3 py-1 rounded">Featured</span>
                  <span className="text-[#3A5544] text-xs font-medium">{featured.category}</span>
                </div>
                <h2 className="font-serif text-3xl text-[#1A1A1A] leading-[1.3] mb-4">{featured.title}</h2>
                <p className="text-[#666] text-sm leading-relaxed mb-6">{featured.excerpt}</p>
                <div className="flex items-center justify-between">
                  <p className="text-[#999] text-xs">{featured.date} · {featured.readTime}</p>
                  <span className="inline-flex items-center gap-1.5 text-[#3A5544] text-sm font-medium cursor-pointer hover:gap-2.5 transition-all duration-200">
                    Read <i className="ri-arrow-right-line" />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Filter */}
      <section className="sticky top-16 z-40 bg-[#F4F3F0] border-b border-[#E0DED9] px-8 lg:px-24 py-4">
        <div className="max-w-6xl mx-auto flex items-center gap-2 overflow-x-auto">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`whitespace-nowrap px-5 py-2 rounded text-sm font-medium transition-colors duration-200 cursor-pointer ${
                activeCategory === cat
                  ? "bg-[#3A5544] text-white"
                  : "bg-white text-[#555] hover:text-[#1A1A1A]"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 px-8 lg:px-24">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {rest.map((article) => (
              <div key={article.id} className="group bg-white rounded-lg overflow-hidden cursor-pointer">
                <div className="h-48 overflow-hidden">
                  <img
                    src={article.img}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <p className="text-[#3A5544] text-xs font-medium uppercase tracking-widest mb-3">{article.category}</p>
                  <h3 className="font-serif text-xl text-[#1A1A1A] leading-[1.35] mb-3">{article.title}</h3>
                  <p className="text-[#777] text-sm leading-relaxed mb-5 line-clamp-2">{article.excerpt}</p>
                  <div className="flex items-center justify-between border-t border-[#F0EEEB] pt-4">
                    <p className="text-[#AAA] text-xs">{article.date} · {article.readTime}</p>
                    <span className="text-[#3A5544] text-xs font-medium cursor-pointer hover:underline">Read →</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
